import { Box } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { FC } from 'react';
import { v4 as uuidv4 } from 'uuid';

import ConditionNode from 'containers/Inventory/ConditionNode';
import GroupNode from 'containers/Inventory/GroupNode';
import { useInventoryFilter } from 'context/InventoryFilterContext';

import {
  InventoryConditionItemName,
  InventoryConditionOperatorName,
  AdvancedInventoryFilterType,
  InventoryGroupTypeOperator,
} from 'constants/enums';
import { InventoryTreeNode } from 'types/customInventoryFilters.interface';

const AdvancedInventoryFilter: FC = () => {
  const {
    rootNode,
    conditionOperatorsMap,
    conditionItemsMap,
    setRootNode,
  } = useInventoryFilter();
  const updateNode = (node: InventoryTreeNode, nodeId: string, updates: Partial<InventoryTreeNode>)
  : InventoryTreeNode => {
    if (node.id === nodeId) {
      return { ...node, ...updates };
    }
    if (node.children) {
      return {
        ...node,
        children: node.children.map((child) => updateNode(child, nodeId, updates)),
      };
    }
    return node;
  };

  const addCondition = (parentId: string) => {
    const newNode: InventoryTreeNode = {
      id: uuidv4(),
      type: AdvancedInventoryFilterType.CONDITION,
      conditionItem: conditionItemsMap[InventoryConditionItemName.ITEM],
      conditionOperator: conditionOperatorsMap[InventoryConditionOperatorName.EQ],
      conditionValue: '',
    };

    const addToTree = (node: InventoryTreeNode): InventoryTreeNode => {
      if (node.id === parentId) {
        return {
          ...node,
          children: [...(node.children || []), newNode],
        };
      }
      if (node.children) {
        return {
          ...node,
          children: node.children.map(addToTree),
        };
      }
      return node;
    };

    setRootNode(addToTree(rootNode));
  };

  const addGroup = (parentId: string) => {
    const newGroup: InventoryTreeNode = {
      id: uuidv4(),
      type: AdvancedInventoryFilterType.GROUP,
      groupOperator: InventoryGroupTypeOperator.AND,
      children: [],
    };

    const addToTree = (node: InventoryTreeNode): InventoryTreeNode => {
      if (node.id === parentId) {
        return {
          ...node,
          children: [...(node.children || []), newGroup],
        };
      }
      if (node.children) {
        return {
          ...node,
          children: node.children.map(addToTree),
        };
      }
      return node;
    };

    setRootNode(addToTree(rootNode));
  };

  const deleteNode = (node: InventoryTreeNode, nodeId: string): InventoryTreeNode | null => {
    if (node.id === nodeId) return null;
    if (node.children) {
      const filteredChildren = node.children
        .map((child) => deleteNode(child, nodeId))
        .filter((child): child is InventoryTreeNode => child !== null);
      return { ...node, children: filteredChildren };
    }
    return node;
  };

  const handleDeleteNode = (nodeId: string) => {
    if (rootNode.id === nodeId) return;
    const updated = deleteNode(rootNode, nodeId);
    if (updated) {
      setRootNode(updated);
    }
  };

  const renderTree = (node: InventoryTreeNode, isRoot = false) => (
    <TreeItem
      key={node.id}
      itemId={node.id}
      label={
        node.type === AdvancedInventoryFilterType.GROUP ? (
          <GroupNode
            node={node}
            isRoot={isRoot}
            onAddCondition={() => addCondition(node.id)}
            onAddGroup={() => addGroup(node.id)}
            onDelete={() => handleDeleteNode(node.id)}
            onUpdateNode={(id, updates) => setRootNode(updateNode(rootNode, id, updates))}
          />
        ) : (
          <ConditionNode
            node={node}
            onDelete={() => handleDeleteNode(node.id)}
            onUpdateNode={(id, updates) => setRootNode(updateNode(rootNode, id, updates))}
          />
        )
      }
    >
      {node.children && node.children.map((child) => renderTree(child))}
    </TreeItem>
  );

  return (
    <Box display="flex" flexDirection="column" width="100%" mt={3}>
      <SimpleTreeView
        disableSelection
        sx={{ maxHeight: 500, overflowY: 'auto' }}
      >
        {renderTree(rootNode, true)}
      </SimpleTreeView>
    </Box>
  );
};

export default AdvancedInventoryFilter;
